/**
 * Web Presence v2 — editor panels (Toolbox, AI generate, Settings, Layers).
 * Unified builder: the toolbox offers the SAME block types as the classic
 * Studio (rendered via BlockNode → PublicSiteBlock) plus Container/Triveny Hero,
 * and AI is built into the canvas (generate → nodes, per-node Edit with AI).
 */

import React, { useEffect, useState } from 'react';
import { Element, useEditor } from '@craftjs/core';
import { Container, TextNode, ImageNode, TrivenyHeroNode, BlockNode, BLOCK_DEFAULTS, FreeContainer, FreeLayer, ShapeNode, VideoBgNode, SvgNode, PathNode } from './nodes';
import {
  Rows, LayoutGrid, Layers as LayersIcon, Video, Sparkles, Square, Circle, Minus, Type, Image as ImageIcon,
  PanelTop, Heading, AlignLeft, MousePointerClick, BarChart3, Quote, HelpCircle, Tag, RectangleHorizontal, TextCursorInput, PenTool, Spline,
} from 'lucide-react';
import { webPresenceApi } from '../../shared/services/platformApi';
import { normalizeAIBlocks } from '../web-presence/utils/normalizeAIBlocks';
import { ImageUpload } from './ImageUpload';

// Brand panel — upload a brand logo + set a brand color scheme. Controlled by
// BuilderShell (which owns the brand state, persists it to the 'brand' Craft
// doc, and provides it via BrandCtx so colors cascade to every block live).
// Connected to the classic wp_design_tokens store (which the AI brand generator
// + Design Tokens tab write to): import brand colors FROM tokens, or push them
// TO tokens, so the two systems stay in sync.
type Brand = { logoSrc?: string; primary?: string; accent?: string; text?: string; background?: string };
type BrandToken = { id?: string; name: string; value: string; group?: string };
// Map a brand field ↔ the design-token names it corresponds to (first match wins).
const TOKEN_ALIASES: Record<keyof Brand, string[]> = {
  primary: ['primary', 'brand', 'accent'],
  accent: ['accent', 'secondary'],
  text: ['ink', 'text', 'foreground'],
  background: ['background', 'bg', 'surface'],
  logoSrc: [],
};
export function BrandPanel({ brand, onChange, tokens = [], siteId = '', onTokensSaved }: { brand: Brand; onChange: (next: Brand) => void; tokens?: BrandToken[]; siteId?: string; onTokensSaved?: () => void }) {
  const { query, actions } = useEditor();
  const [status, setStatus] = useState('');
  const set = (patch: Brand) => onChange({ ...brand, ...patch });
  const colorTokens = tokens.filter((t) => (t.group || 'color') === 'color' && /^#|rgb|hsl/i.test(t.value || ''));
  const applyLogoToHero = () => {
    const nodes = query.getNodes();
    let n = 0;
    Object.keys(nodes).forEach((id) => { if (nodes[id]?.data?.name === 'TrivenyHeroNode') { actions.setProp(id, (p: any) => { p.logoSrc = brand.logoSrc || ''; }); n += 1; } });
    setStatus(n ? `Applied to ${n} hero block(s)` : 'No Triveny hero on this page');
    window.setTimeout(() => setStatus(''), 2500);
  };
  // Pull brand colors from the org's design tokens (e.g. an AI-generated palette).
  const importFromTokens = () => {
    const find = (names: string[]) => colorTokens.find((t) => names.includes((t.name || '').toLowerCase()))?.value;
    const next: Brand = { ...brand };
    (['primary', 'accent', 'text', 'background'] as Array<keyof Brand>).forEach((k) => {
      const v = find(TOKEN_ALIASES[k]); if (v) (next as any)[k] = v;
    });
    onChange(next);
    setStatus('Imported colors from design tokens');
    window.setTimeout(() => setStatus(''), 2500);
  };
  // Push the brand colors back to design tokens (update by name, else create) so
  // the Design Tokens tab + AI tools share them.
  const saveToTokens = async () => {
    if (!siteId) { setStatus('No site to save tokens to'); window.setTimeout(() => setStatus(''), 2500); return; }
    setStatus('Saving to design tokens…');
    const pairs: Array<[string, string | undefined]> = [['primary', brand.primary], ['accent', brand.accent], ['ink', brand.text], ['background', brand.background]];
    try {
      for (const [name, value] of pairs) {
        if (!value) continue;
        const existing = tokens.find((t) => (t.name || '').toLowerCase() === name && (t.group || 'color') === 'color');
        if (existing?.id) await webPresenceApi.updateDesignToken(existing.id, { token_value: value });
        else await webPresenceApi.createDesignToken({ site_id: siteId, token_group: 'color', token_name: name, token_value: value, token_label: name[0].toUpperCase() + name.slice(1) });
      }
      onTokensSaved?.();
      setStatus('Saved to design tokens ✓');
    } catch { setStatus('Save to tokens failed'); }
    window.setTimeout(() => setStatus(''), 3000);
  };
  const colorRow = (label: string, key: keyof Brand) => (
    <label style={{ fontSize: 12, color: '#6b7280' }}>{label}
      <span style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test((brand[key] as string) || '') ? (brand[key] as string) : '#ffffff'} onChange={(e) => set({ [key]: e.target.value } as Brand)} style={{ width: 36, height: 30, padding: 0, border: '1px solid #d7dbe0', borderRadius: 6, cursor: 'pointer' }} />
        <input type="text" value={(brand[key] as string) || ''} placeholder="#hex" onChange={(e) => set({ [key]: e.target.value || undefined } as Brand)} style={{ flex: 1, border: '1px solid #d7dbe0', borderRadius: 6, padding: '4px 8px', fontSize: 12.5 }} />
      </span>
    </label>
  );
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9aa3af' }}>Brand</div>
      <ImageUpload label="Logo (upload or URL)" value={brand.logoSrc || ''} onChange={(v) => set({ logoSrc: v })} />
      <button onClick={applyLogoToHero} style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #c7b8f0', background: '#f7f4ff', color: '#5b4b9e', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Apply logo to hero</button>
      {colorRow('Primary', 'primary')}
      {colorRow('Accent', 'accent')}
      {colorRow('Text', 'text')}
      {colorRow('Background', 'background')}
      {/* Design-token sync — one connected system with the Design Tokens tab + AI */}
      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
        <button onClick={importFromTokens} disabled={!colorTokens.length} title={colorTokens.length ? '' : 'No design tokens found'} style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #d7dbe0', background: '#fff', color: '#14171c', fontSize: 11.5, fontWeight: 600, cursor: colorTokens.length ? 'pointer' : 'default', opacity: colorTokens.length ? 1 : 0.5 }}>↓ From tokens</button>
        <button onClick={saveToTokens} style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #d7dbe0', background: '#fff', color: '#14171c', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>↑ To tokens</button>
      </div>
      {colorTokens.length > 0 && (
        <span style={{ fontSize: 11, color: '#9aa3af' }}>{colorTokens.length} design token color(s) available as swatches on every block.</span>
      )}
      {status && <span style={{ fontSize: 12, color: status.includes('No') || status.includes('fail') ? '#dc2626' : '#16a34a' }}>{status}</span>}
    </div>
  );
}

// Per-page SEO + custom code snippets — persisted in the page's craft `meta`
// ({seo:{title,description,ogImage}, scripts:{head,bodyEnd}}). Applied on the
// public render (document.title + meta tags + injected <script>/<style>). Saving
// here also serializes the current document (same as Save) so meta + doc stay in
// sync; nothing goes live until the page is Deployed.
type PageMeta = { seo?: { title?: string; description?: string; ogImage?: string }; scripts?: { head?: string; bodyEnd?: string } };
export function PageMetaPanel({ docKey }: { docKey: string }) {
  const { query } = useEditor();
  const [meta, setMeta] = useState<PageMeta>({});
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    webPresenceApi.craftGet(docKey).then((r: any) => { const m = r?.data?.meta; if (m && typeof m === 'object') setMeta(m); }).catch(() => {});
  }, [docKey]);
  const seo = meta.seo || {};
  const scripts = meta.scripts || {};
  const setSeo = (patch: any) => setMeta((m) => ({ ...m, seo: { ...m.seo, ...patch } }));
  const setScripts = (patch: any) => setMeta((m) => ({ ...m, scripts: { ...m.scripts, ...patch } }));
  const save = async () => {
    setStatus('Saving…');
    try {
      const res: any = await webPresenceApi.craftSave(JSON.parse(query.serialize()), docKey, meta);
      setStatus(res?.success === false ? 'Save failed (sign in?)' : 'Saved ✓ — Deploy to go live');
    } catch { setStatus('Save failed'); }
    window.setTimeout(() => setStatus(''), 3000);
  };
  const fieldCss: React.CSSProperties = { width: '100%', marginTop: 4, border: '1px solid #d7dbe0', borderRadius: 6, padding: '6px 8px', fontSize: 12.5 };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9aa3af' }}>SEO &amp; Code</span>
        <span style={{ fontSize: 12, color: '#9aa3af' }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <>
          <label style={{ fontSize: 12, color: '#6b7280' }}>Page title (SEO)
            <input style={fieldCss} value={seo.title || ''} onChange={(e) => setSeo({ title: e.target.value })} placeholder="Shown in the browser tab & search results" />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>Meta description
            <textarea style={{ ...fieldCss, resize: 'vertical' }} rows={2} value={seo.description || ''} onChange={(e) => setSeo({ description: e.target.value })} placeholder="~155 chars; shown under the title in search results" />
          </label>
          <ImageUpload label="Social share image (og:image)" value={seo.ogImage || ''} onChange={(v) => setSeo({ ogImage: v })} />
          <label style={{ fontSize: 12, color: '#6b7280' }}>Head code (analytics, fonts, meta, &lt;style&gt;)
            <textarea style={{ ...fieldCss, fontFamily: 'ui-monospace, monospace', resize: 'vertical' }} rows={3} value={scripts.head || ''} onChange={(e) => setScripts({ head: e.target.value })} placeholder="<script>…</script> or <style>…</style>" />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>End-of-body code
            <textarea style={{ ...fieldCss, fontFamily: 'ui-monospace, monospace', resize: 'vertical' }} rows={3} value={scripts.bodyEnd || ''} onChange={(e) => setScripts({ bodyEnd: e.target.value })} placeholder="<script>…</script> loaded after the page" />
          </label>
          <button onClick={save} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #c7b8f0', background: '#f7f4ff', color: '#5b4b9e', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Save SEO &amp; code</button>
          {status && <span style={{ fontSize: 12, color: status.includes('fail') ? '#dc2626' : '#16a34a' }}>{status}</span>}
        </>
      )}
    </div>
  );
}

const card: React.CSSProperties = {
  border: '1px solid #e6e8ec', borderRadius: 8, padding: '8px 11px',
  fontSize: 12.5, color: '#14171c', cursor: 'grab', background: '#fff', userSelect: 'none',
};

// Flat block types offered in the unified toolbox (rendered via BlockNode).
const BLOCK_TYPES: Array<[string, string]> = [
  ['Hero', 'hero'], ['Heading', 'heading'], ['Text', 'text'], ['Features', 'features'],
  ['CTA', 'cta'], ['Stats', 'stats'], ['Testimonials', 'testimonials'], ['FAQ', 'faq'],
  ['Pricing', 'pricing'], ['Image', 'image'], ['Video', 'video'], ['Button', 'button'],
  ['Form', 'form'],
];

// Toolbox — drag a chip onto the canvas to create that node.
// Icon per block type so the Insert palette reads visually, not as a wall of text.
const BLOCK_ICON: Record<string, any> = {
  hero: PanelTop, heading: Heading, text: AlignLeft, features: LayoutGrid, cta: MousePointerClick,
  stats: BarChart3, testimonials: Quote, faq: HelpCircle, pricing: Tag, image: ImageIcon,
  video: Video, button: RectangleHorizontal, form: TextCursorInput,
};

type ToolItem = { label: string; icon: any; el: React.ReactElement };
const tile: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
  padding: '11px 4px', border: '1px solid #eef0f3', borderRadius: 9, cursor: 'grab',
  background: '#fff', color: '#4a5160', userSelect: 'none', textAlign: 'center',
};
const grpTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9aa3af', marginBottom: 2 };

function ToolGroup({ title, items, create }: { title: string; items: ToolItem[]; create: (ref: HTMLElement, el: React.ReactElement) => void }) {
  return (
    <div>
      <div style={grpTitle}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {items.map(({ label, icon: Icon, el }) => (
          <div key={label} ref={(ref) => ref && create(ref, el)} title={label} style={tile}>
            <Icon size={18} strokeWidth={1.8} />
            <span style={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.15 }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Toolbox() {
  const { connectors } = useEditor();
  const create = (ref: HTMLElement, el: React.ReactElement) => connectors.create(ref, el);
  const structural: ToolItem[] = [
    { label: 'Container', icon: Rows, el: <Element is={Container} canvas /> },
    { label: 'Free canvas', icon: LayoutGrid, el: <Element is={FreeContainer} canvas /> },
    { label: 'Layer', icon: LayersIcon, el: <Element is={FreeLayer} canvas /> },
    { label: 'Video bg', icon: Video, el: <Element is={VideoBgNode} canvas /> },
    { label: 'Hero', icon: Sparkles, el: <Element is={TrivenyHeroNode} /> },
  ];
  const elements: ToolItem[] = [
    { label: 'Rectangle', icon: Square, el: <Element is={ShapeNode} shape="rectangle" /> },
    { label: 'Ellipse', icon: Circle, el: <Element is={ShapeNode} shape="ellipse" /> },
    { label: 'Line', icon: Minus, el: <Element is={ShapeNode} shape="line" /> },
    { label: 'Text', icon: Type, el: <Element is={TextNode} /> },
    { label: 'Image', icon: ImageIcon, el: <Element is={ImageNode} /> },
    { label: 'SVG / Logo', icon: PenTool, el: <Element is={SvgNode} /> },
    { label: 'Path', icon: Spline, el: <Element is={PathNode} /> },
  ];
  const blocks: ToolItem[] = BLOCK_TYPES.map(([label, bt]) => ({
    label,
    icon: BLOCK_ICON[bt] || Square,
    el: <Element is={BlockNode} blockType={bt} content={BLOCK_DEFAULTS[bt] || {}} styles={{}} />,
  }));
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <ToolGroup title="Layout" items={structural} create={create} />
      <ToolGroup title="Elements" items={elements} create={create} />
      <ToolGroup title="Blocks" items={blocks} create={create} />
    </div>
  );
}

// AI generate — built into the canvas. Generate a page/section from a prompt;
// blocks are inserted as BlockNodes (same renderer as the public site).
export function AIGeneratePanel() {
  const { actions, query } = useEditor();
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setStatus('Generating…');
    try {
      const res: any = await webPresenceApi.aiGenerate({ content_type: 'web_page', prompt: prompt.trim(), model_preference: 'quality' });
      let blocks: any = res?.data?.generated?.blocks;
      if (typeof blocks === 'string') { try { blocks = JSON.parse(blocks); } catch { blocks = []; } }
      const normalized = normalizeAIBlocks(Array.isArray(blocks) ? blocks : [], '');
      if (!normalized.length) { setStatus('No blocks returned'); }
      else {
        normalized.forEach((b: any) => {
          const tree = query
            .parseReactElement(<Element is={BlockNode} blockType={b.blockType} content={b.content} styles={b.styles || {}} />)
            .toNodeTree();
          actions.addNodeTree(tree, 'ROOT');
        });
        setStatus(`Added ${normalized.length} block${normalized.length === 1 ? '' : 's'} ✓`);
        setPrompt('');
      }
    } catch {
      setStatus('Generate failed (sign in?)');
    }
    setBusy(false);
    window.setTimeout(() => setStatus(''), 3000);
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9aa3af' }}>AI</div>
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Describe a page or section to generate…"
        rows={3}
        style={{ width: '100%', border: '1px solid #d7dbe0', borderRadius: 7, padding: 8, fontSize: 12.5, resize: 'vertical' }}
      />
      <button
        onClick={generate}
        disabled={busy || !prompt.trim()}
        style={{ padding: '8px 12px', borderRadius: 7, border: 'none', background: busy ? '#9aa3af' : '#7468ae', color: '#fff', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}
      >
        {busy ? 'Generating…' : 'Generate with AI'}
      </button>
      {status && <span style={{ fontSize: 12, color: status.includes('fail') || status.includes('No') ? '#dc2626' : '#16a34a' }}>{status}</span>}
    </div>
  );
}

// Settings — renders the selected node's settings + per-node "Edit with AI".
export function SettingsPanel() {
  const { selected, actions } = useEditor((state, query) => {
    const id = (state.events.selected && Array.from(state.events.selected)[0]) as string | undefined;
    let selected: any;
    if (id && state.nodes[id]) {
      const node = state.nodes[id];
      selected = {
        id,
        name: node.data.displayName || node.data.name,
        nodeName: node.data.name,
        props: node.data.props,
        settings: node.related && (node.related as any).settings,
        isDeletable: query.node(id).isDeletable(),
      };
    }
    return { selected };
  });

  const [aiBusy, setAiBusy] = useState(false);

  if (!selected) {
    return <div style={{ fontSize: 13, color: '#9aa3af' }}>Select an element to edit its settings.</div>;
  }

  const isBlock = selected.nodeName === 'BlockNode' || !!selected.props?.blockType;
  const editWithAI = async () => {
    const instruction = window.prompt('Edit with AI — describe the change (e.g. "make it colorful"):');
    if (!instruction) return;
    setAiBusy(true);
    try {
      const res: any = await webPresenceApi.aiEditBlock({
        block: { blockType: selected.props.blockType, content: selected.props.content, styles: selected.props.styles || {} },
        instruction,
      });
      const data = res?.data ?? {};
      // Backend returns { success, block: { blockType, content, styles } }. ONLY
      // apply when we got a usable block — and merge content/styles, never
      // replace content with the raw response (that's what made blocks vanish).
      const edited = data.block;
      const ok = data.success !== false && edited && typeof edited === 'object' && (edited.content || edited.styles);
      if (ok) {
        actions.setProp(selected.id, (p: any) => {
          if (edited.content && typeof edited.content === 'object') p.content = edited.content;
          if (edited.styles && typeof edited.styles === 'object') p.styles = { ...(p.styles || {}), ...edited.styles };
        });
      } else {
        window.alert(`AI couldn’t apply that edit${data.error ? `: ${data.error}` : '. Try rephrasing, or use the Style controls below.'}`);
      }
    } catch {
      window.alert('AI edit failed — check you’re signed in, then try again. Your block is unchanged.');
    }
    setAiBusy(false);
  };

  const Settings = selected.settings;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.name}</span>
        {selected.isDeletable && (
          <button
            onClick={() => actions.delete(selected.id)}
            style={{ fontSize: 12, color: '#dc2626', border: '1px solid #fca5a5', background: '#fff', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
          >
            Delete
          </button>
        )}
      </div>
      {isBlock && (
        <button
          onClick={editWithAI}
          disabled={aiBusy}
          style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #c7b8f0', background: '#f7f4ff', color: '#5b4b9e', fontSize: 12.5, fontWeight: 600, cursor: aiBusy ? 'default' : 'pointer' }}
        >
          {aiBusy ? 'Editing…' : '✨ Edit with AI'}
        </button>
      )}
      {Settings ? <Settings /> : <div style={{ fontSize: 12, color: '#9aa3af' }}>No settings for this element.</div>}
    </div>
  );
}
